import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import SEO from '../components/SEO';

const AccordionItem = ({ title, children, icon, isOpen, onToggle }) => {
    return (
        <div className="bg-white dark:bg-stone-900 rounded-2xl border border-stone-200/80 dark:border-stone-800 shadow-soft overflow-hidden transition-all duration-200">
            <button
                onClick={onToggle}
                className="w-full flex justify-between items-center text-left p-6 sm:p-7 hover:bg-stone-50 dark:hover:bg-stone-800/50 transition-colors"
                aria-expanded={isOpen}
            >
                <div className="flex items-center pr-4">
                    {icon && <span className="text-xl sm:text-2xl mr-4 flex-shrink-0">{icon}</span>}
                    <span className="text-base sm:text-lg font-bold text-stone-900 dark:text-stone-100 font-display">
                        {title}
                    </span>
                </div>
                <div className={`w-8 h-8 rounded-full bg-stone-100 dark:bg-stone-800 flex items-center justify-center flex-shrink-0 text-terracotta transition-transform duration-300 ${isOpen ? 'rotate-180 bg-terracotta-50 text-terracotta' : ''}`}>
                    <svg 
                        className="w-4 h-4" 
                        fill="none" 
                        stroke="currentColor" 
                        viewBox="0 0 24 24"
                    >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                </div>
            </button>
            {isOpen && (
                <div className="px-6 sm:px-7 pb-6 text-stone-600 dark:text-stone-300 leading-relaxed text-sm sm:text-base border-t border-stone-100 dark:border-stone-800/80 pt-4">
                    {children}
                </div>
            )}
        </div>
    );
};

const ServicesPage = () => {
    const { t } = useTranslation();
    const [openFaqIndex, setOpenFaqIndex] = useState(0);

    const serviceAreas = [
        { 
            title: t('services.areas_data.panic.title'), 
            icon: (
                <svg className="w-6 h-6 text-terracotta" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
            ),
            content: t('services.areas_data.panic.content'),
            techniques: [t('services.areas_data.panic.t1'), t('services.areas_data.panic.t2'), t('services.areas_data.panic.t3')],
            highlight: t('services.areas_data.panic.highlight')
        },
        { 
            title: t('services.areas_data.anxiety.title'), 
            icon: (
                <svg className="w-6 h-6 text-terracotta" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.828 14.828a4 4 0 01-5.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
            ),
            content: t('services.areas_data.anxiety.content'),
            techniques: [t('services.areas_data.anxiety.t1'), t('services.areas_data.anxiety.t2'), t('services.areas_data.anxiety.t3')],
            highlight: t('services.areas_data.anxiety.highlight')
        },
        { 
            title: t('services.areas_data.depression.title'), 
            icon: (
                <svg className="w-6 h-6 text-terracotta" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
                </svg>
            ),
            content: t('services.areas_data.depression.content'),
            techniques: [t('services.areas_data.depression.t1'), t('services.areas_data.depression.t2'), t('services.areas_data.depression.t3')],
            highlight: t('services.areas_data.depression.highlight')
        },
        { 
            title: t('services.areas_data.couple.title'), 
            icon: (
                <svg className="w-6 h-6 text-terracotta" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
                </svg>
            ),
            content: t('services.areas_data.couple.content'),
            techniques: [t('services.areas_data.couple.t1'), t('services.areas_data.couple.t2'), t('services.areas_data.couple.t3')],
            highlight: t('services.areas_data.couple.highlight')
        },
        { 
            title: t('services.areas_data.trauma.title'), 
            icon: (
                <svg className="w-6 h-6 text-terracotta" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
            ),
            content: t('services.areas_data.trauma.content'),
            techniques: [t('services.areas_data.trauma.t1'), t('services.areas_data.trauma.t2'), t('services.areas_data.trauma.t3')],
            highlight: t('services.areas_data.trauma.highlight')
        },
        { 
            title: t('services.areas_data.growth.title'), 
            icon: (
                <svg className="w-6 h-6 text-terracotta" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.746 0 3.332.477 4.5 1.253v13C19.832 18.477 18.246 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                </svg>
            ),
            content: t('services.areas_data.growth.content'),
            techniques: [t('services.areas_data.growth.t1'), t('services.areas_data.growth.t2'), t('services.areas_data.growth.t3')],
            highlight: t('services.areas_data.growth.highlight')
        },
    ];
    
    const faqs = [
        { 
            q: t('services.faqs_data.q1.q'), 
            a: t('services.faqs_data.q1.a')
        },
        { 
            q: t('services.faqs_data.q2.q'), 
            a: t('services.faqs_data.q2.a') 
        },
        { 
            q: t('services.faqs_data.q3.q'), 
            a: t('services.faqs_data.q3.a') 
        },
        { 
            q: t('services.faqs_data.q4.q'), 
            a: t('services.faqs_data.q4.a') 
        },
        { 
            q: t('services.faqs_data.q5.q'), 
            a: t('services.faqs_data.q5.a')
        }
    ];

    return (
        <>
            <SEO 
                title="Servicii Psihoterapie București & Online - Oana Tenea"
                description="Consiliere psihologică individuală, terapie de cuplu (EFT), tratament anxietate, depresie, traume și Schema Therapy în cabinet în București (Calea Dorobanți) și online."
                path="/servicii"
                faqItems={faqs}
            />
            
            <div className="bg-stone-50 dark:bg-stone-950 text-stone-800 dark:text-stone-200 py-16 sm:py-24 transition-colors duration-300">
                <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
                    
                    {/* Hero Section */}
                    <div className="text-center max-w-3xl mx-auto mb-16 sm:mb-20">
                        <span className="inline-flex items-center px-4 py-1.5 rounded-full bg-terracotta-50 dark:bg-stone-800 text-terracotta dark:text-terracotta-light text-xs font-semibold tracking-wider uppercase mb-4 border border-terracotta-200/60 dark:border-stone-700">
                            Servicii & Metodologie
                        </span>
                        <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-bold text-stone-900 dark:text-stone-100 tracking-tight">
                            {t('services.title')}
                        </h1>
                        <p className="text-stone-600 dark:text-stone-300 text-base sm:text-lg lg:text-xl leading-relaxed mt-6">
                            {t('services.subtitle')}
                        </p>
                        <div className="w-16 h-1 bg-terracotta mx-auto mt-6 rounded-full"></div>
                    </div>

                    {/* FORMATS: In-Person (Dorobanți) vs Online Sessions */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-24 max-w-5xl mx-auto">
                        
                        {/* In-Person Cabinet */}
                        <div className="bg-white dark:bg-stone-900 rounded-3xl p-8 sm:p-10 shadow-elevated border border-stone-200/80 dark:border-stone-800 relative overflow-hidden flex flex-col justify-between">
                            <div className="absolute top-0 right-0 w-32 h-32 bg-terracotta/5 rounded-full blur-2xl pointer-events-none"></div>

                            <div>
                                <div className="flex items-center gap-4 mb-6">
                                    <div className="w-12 h-12 rounded-2xl bg-terracotta/10 text-terracotta flex items-center justify-center">
                                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                                        </svg>
                                    </div>
                                    <div>
                                        <h2 className="font-display text-2xl font-bold text-stone-900 dark:text-stone-100">
                                            {t('services.in_person')}
                                        </h2>
                                        <span className="text-xs text-stone-500 dark:text-stone-400 font-medium">
                                            București, Calea Dorobanți 116-122
                                        </span>
                                    </div>
                                </div>

                                <p className="text-stone-600 dark:text-stone-300 text-sm sm:text-base leading-relaxed mb-8">
                                    {t('services.in_person_desc')}
                                </p>

                                <div className="space-y-3.5 mb-8">
                                    <div className="flex items-center text-stone-700 dark:text-stone-300 text-sm">
                                        <div className="w-5 h-5 rounded-full bg-terracotta/15 text-terracotta flex items-center justify-center mr-3 flex-shrink-0">
                                            <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
                                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                            </svg>
                                        </div>
                                        <span>{t('services.individual')}</span>
                                    </div>

                                    <div className="flex items-center text-stone-700 dark:text-stone-300 text-sm">
                                        <div className="w-5 h-5 rounded-full bg-terracotta/15 text-terracotta flex items-center justify-center mr-3 flex-shrink-0">
                                            <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
                                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                            </svg>
                                        </div>
                                        <span>{t('services.couple')}</span>
                                    </div>

                                    <div className="flex items-center text-stone-700 dark:text-stone-300 text-sm">
                                        <div className="w-5 h-5 rounded-full bg-terracotta/15 text-terracotta flex items-center justify-center mr-3 flex-shrink-0">
                                            <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
                                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                            </svg>
                                        </div>
                                        <span>{t('services.initial')}</span>
                                    </div>
                                </div>
                            </div>

                            <Link
                                to="/contact"
                                className="w-full inline-flex items-center justify-center px-6 py-3.5 bg-terracotta hover:bg-terracotta-dark text-white font-semibold rounded-full shadow-warm transition-all duration-200 text-sm"
                            >
                                Programează la Cabinet
                            </Link>
                        </div>

                        {/* Online Therapy */}
                        <div className="bg-white dark:bg-stone-900 rounded-3xl p-8 sm:p-10 shadow-elevated border border-stone-200/80 dark:border-stone-800 relative overflow-hidden flex flex-col justify-between">
                            <div className="absolute top-0 right-0 w-32 h-32 bg-sage/10 rounded-full blur-2xl pointer-events-none"></div>

                            <div>
                                <div className="flex items-center gap-4 mb-6">
                                    <div className="w-12 h-12 rounded-2xl bg-sage-100 text-sage-800 dark:bg-stone-800 dark:text-sage-300 flex items-center justify-center">
                                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                        </svg>
                                    </div>
                                    <div>
                                        <h2 className="font-display text-2xl font-bold text-stone-900 dark:text-stone-100">
                                            {t('services.online')}
                                        </h2>
                                        <span className="text-xs text-stone-500 dark:text-stone-400 font-medium">
                                            Video-ședințe securizate oriunde te afli
                                        </span>
                                    </div>
                                </div>

                                <p className="text-stone-600 dark:text-stone-300 text-sm sm:text-base leading-relaxed mb-8">
                                    {t('services.online_desc')}
                                </p>

                                <div className="space-y-3.5 mb-8">
                                    <div className="flex items-center text-stone-700 dark:text-stone-300 text-sm">
                                        <div className="w-5 h-5 rounded-full bg-sage-100 dark:bg-stone-800 text-sage-700 dark:text-sage-300 flex items-center justify-center mr-3 flex-shrink-0">
                                            <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
                                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                            </svg>
                                        </div>
                                        <span>{t('services.home_access')}</span>
                                    </div>

                                    <div className="flex items-center text-stone-700 dark:text-stone-300 text-sm">
                                        <div className="w-5 h-5 rounded-full bg-sage-100 dark:bg-stone-800 text-sage-700 dark:text-sage-300 flex items-center justify-center mr-3 flex-shrink-0">
                                            <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
                                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                            </svg>
                                        </div>
                                        <span>{t('services.flex_schedule')}</span>
                                    </div>

                                    <div className="flex items-center text-stone-700 dark:text-stone-300 text-sm">
                                        <div className="w-5 h-5 rounded-full bg-sage-100 dark:bg-stone-800 text-sage-700 dark:text-sage-300 flex items-center justify-center mr-3 flex-shrink-0">
                                            <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
                                                <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                            </svg>
                                        </div>
                                        <span>{t('services.quality')}</span>
                                    </div>
                                </div>
                            </div>

                            <Link
                                to="/contact"
                                className="w-full inline-flex items-center justify-center px-6 py-3.5 bg-stone-900 hover:bg-stone-800 dark:bg-stone-800 dark:hover:bg-stone-700 text-white font-semibold rounded-full shadow-soft transition-all duration-200 text-sm"
                            >
                                Programează Ședință Online
                            </Link>
                        </div>

                    </div>
                
                    {/* THERAPEUTIC METHODOLOGY SECTION */}
                    <div className="max-w-6xl mx-auto mb-24">
                        <div className="bg-white dark:bg-stone-900 rounded-3xl p-8 sm:p-12 shadow-elevated border border-stone-200/80 dark:border-stone-800">
                            <div className="text-center max-w-3xl mx-auto mb-12">
                                <span className="text-xs font-semibold uppercase tracking-wider text-terracotta mb-2 block">
                                    Metodologie Validată Științific
                                </span>
                                <h2 className="font-display text-3xl sm:text-4xl font-bold text-stone-900 dark:text-stone-100 mb-4">
                                    {t('services.approach')}
                                </h2>
                                <p className="text-stone-600 dark:text-stone-400 text-base sm:text-lg">
                                    {t('services.approach_sub')}
                                </p>
                            </div>
                            
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                <div className="p-6 rounded-2xl bg-stone-50 dark:bg-stone-800/60 border border-stone-200/60 dark:border-stone-700">
                                    <div className="flex items-center gap-3 mb-3">
                                        <div className="w-8 h-8 rounded-lg bg-terracotta/10 text-terracotta flex items-center justify-center font-bold text-xs">
                                            01
                                        </div>
                                        <h3 className="font-display text-xl font-bold text-stone-900 dark:text-stone-100">
                                            {t('services.app1_title')}
                                        </h3>
                                    </div>
                                    <p className="text-stone-600 dark:text-stone-300 text-sm leading-relaxed">
                                        {t('services.app1_desc')}
                                    </p>
                                </div>
                                
                                <div className="p-6 rounded-2xl bg-stone-50 dark:bg-stone-800/60 border border-stone-200/60 dark:border-stone-700">
                                    <div className="flex items-center gap-3 mb-3">
                                        <div className="w-8 h-8 rounded-lg bg-terracotta/10 text-terracotta flex items-center justify-center font-bold text-xs">
                                            02
                                        </div>
                                        <h3 className="font-display text-xl font-bold text-stone-900 dark:text-stone-100">
                                            {t('services.app2_title')}
                                        </h3>
                                    </div>
                                    <p className="text-stone-600 dark:text-stone-300 text-sm leading-relaxed">
                                        {t('services.app2_desc')}
                                    </p>
                                </div>
                                
                                <div className="p-6 rounded-2xl bg-stone-50 dark:bg-stone-800/60 border border-stone-200/60 dark:border-stone-700">
                                    <div className="flex items-center gap-3 mb-3">
                                        <div className="w-8 h-8 rounded-lg bg-terracotta/10 text-terracotta flex items-center justify-center font-bold text-xs">
                                            03
                                        </div>
                                        <h3 className="font-display text-xl font-bold text-stone-900 dark:text-stone-100">
                                            {t('services.app3_title')}
                                        </h3>
                                    </div>
                                    <p className="text-stone-600 dark:text-stone-300 text-sm leading-relaxed">
                                        {t('services.app3_desc')}
                                    </p>
                                </div>
                                
                                <div className="p-6 rounded-2xl bg-stone-50 dark:bg-stone-800/60 border border-stone-200/60 dark:border-stone-700">
                                    <div className="flex items-center gap-3 mb-3">
                                        <div className="w-8 h-8 rounded-lg bg-terracotta/10 text-terracotta flex items-center justify-center font-bold text-xs">
                                            04
                                        </div>
                                        <h3 className="font-display text-xl font-bold text-stone-900 dark:text-stone-100">
                                            {t('services.app4_title')}
                                        </h3>
                                    </div>
                                    <p className="text-stone-600 dark:text-stone-300 text-sm leading-relaxed">
                                        {t('services.app4_desc')}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                
                    {/* SERVICE SPECIALIZATION AREAS */}
                    <div className="max-w-6xl mx-auto mb-24">
                        <div className="text-center max-w-3xl mx-auto mb-14">
                            <span className="text-xs font-semibold uppercase tracking-wider text-terracotta mb-2 block">
                                Domenii de Intervenție
                            </span>
                            <h2 className="font-display text-3xl sm:text-4xl font-bold text-stone-900 dark:text-stone-100 mb-4">
                                {t('services.areas')}
                            </h2>
                            <p className="text-stone-600 dark:text-stone-400 text-base sm:text-lg">
                                {t('services.areas_sub')}
                            </p>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                            {serviceAreas.map((area, index) => (
                                <div 
                                    key={index} 
                                    className="bg-white dark:bg-stone-900 rounded-3xl p-7 border border-stone-200/80 dark:border-stone-800 shadow-soft hover:shadow-warm transition-all duration-300 transform hover:-translate-y-1 flex flex-col justify-between"
                                >
                                    <div>
                                        <div className="w-12 h-12 rounded-2xl bg-terracotta-50 dark:bg-stone-800 flex items-center justify-center mb-5">
                                            {area.icon}
                                        </div>

                                        <h3 className="font-display text-xl font-bold text-stone-900 dark:text-stone-100 mb-3">
                                            {area.title}
                                        </h3>

                                        <p className="text-stone-600 dark:text-stone-400 text-sm mb-5 leading-relaxed">
                                            {area.content}
                                        </p>
                                        
                                        {area.highlight && (
                                            <div className="bg-terracotta-50/70 dark:bg-stone-800/80 border border-terracotta-200/60 dark:border-stone-700 rounded-xl p-3 mb-5">
                                                <p className="text-terracotta text-xs font-semibold">
                                                    {area.highlight}
                                                </p>
                                            </div>
                                        )}
                                    </div>
                                    
                                    <div className="pt-4 border-t border-stone-100 dark:border-stone-800/80">
                                        <p className="text-[11px] font-bold text-stone-500 dark:text-stone-400 uppercase tracking-wider mb-2.5">
                                            {t('services.methods')}
                                        </p>
                                        <div className="space-y-1.5">
                                            {area.techniques.map((technique, idx) => (
                                                <div key={idx} className="flex items-center text-xs text-stone-600 dark:text-stone-300">
                                                    <span className="w-1.5 h-1.5 bg-terracotta rounded-full mr-2 flex-shrink-0"></span>
                                                    <span>{technique}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* FREQUENTLY ASKED QUESTIONS */}
                    <div className="max-w-4xl mx-auto mb-20">
                        <div className="text-center mb-14">
                            <span className="text-xs font-semibold uppercase tracking-wider text-terracotta mb-2 block">
                                Răspunsuri Clare
                            </span>
                            <h2 className="font-display text-3xl sm:text-4xl font-bold text-stone-900 dark:text-stone-100 mb-4">
                                {t('services.faq')}
                            </h2>
                            <p className="text-stone-600 dark:text-stone-400 text-base sm:text-lg">
                                {t('services.faq_sub')}
                            </p>
                        </div>

                        <div className="space-y-4">
                            {faqs.map((faq, index) => (
                                <AccordionItem 
                                    key={index} 
                                    title={faq.q}
                                    isOpen={openFaqIndex === index}
                                    onToggle={() => setOpenFaqIndex(openFaqIndex === index ? -1 : index)}
                                >
                                    <p>{faq.a}</p>
                                </AccordionItem>
                            ))}
                        </div>
                    </div>

                    {/* CTA SECTION */}
                    <div className="max-w-4xl mx-auto text-center">
                        <div className="bg-gradient-to-br from-terracotta to-warm-orange rounded-3xl p-10 sm:p-12 shadow-elevated text-white">
                            <h3 className="font-display text-3xl sm:text-4xl font-bold mb-4">
                                {t('services.cta_title')}
                            </h3>
                            <p className="mb-8 text-white/90 text-base sm:text-lg max-w-xl mx-auto leading-relaxed">
                                {t('services.cta_desc')}
                            </p>
                            <Link 
                                to="/contact" 
                                className="inline-flex items-center px-9 py-4 bg-white text-terracotta-dark font-bold rounded-full hover:bg-stone-50 transition-all duration-200 transform hover:-translate-y-0.5 shadow-lg text-base"
                            >
                                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                                </svg>
                                {t('services.cta_btn')}
                            </Link>
                        </div>
                    </div>

                </div>
            </div>
        </>
    );
};

export default ServicesPage;
